export type Category = { id: string; label: string; emoji: string }
export type SampleQuestion = { categoryId: string; text: string }

export const CATEGORIES: Category[] = [
  { id: 'patients', label: 'Patients', emoji: '👥' },
  { id: 'registration', label: 'Registration', emoji: '📋' },
  { id: 'appointments', label: 'Appointments', emoji: '📅' },
  { id: 'billing', label: 'Billing', emoji: '💳' },
  { id: 'careCoord', label: 'Care Coord', emoji: '🤝' },
  { id: 'fax', label: 'Fax & Documents', emoji: '📠' },
  { id: 'messages', label: 'Messages', emoji: '💬' },
  { id: 'patientSummary', label: 'Patient Summary', emoji: '🧑‍⚕️' },
  { id: 'clinical', label: 'Clinical', emoji: '🩺' },
  { id: 'graphs', label: 'Graphs & KPIs', emoji: '📊' },
]

export const SAMPLE_QUESTIONS: SampleQuestion[] = [
  { categoryId: 'patients', text: 'How many active patients do we have?' },
  { categoryId: 'patients', text: 'How many invited patients do we have?' },
  { categoryId: 'patients', text: 'How many new patients registered this week?' },
  { categoryId: 'patients', text: 'Show patients created in the last 30 days.' },
  { categoryId: 'patients', text: 'Show patients by state.' },
  { categoryId: 'patients', text: 'Which patients are missing phone numbers?' },
  { categoryId: 'patients', text: 'Which patients are missing email addresses?' },
  { categoryId: 'patients', text: 'Show patients with incomplete demographics.' },
  { categoryId: 'patients', text: 'Find duplicate patients by name and date of birth.' },
  { categoryId: 'patients', text: 'Show patients with missing preferred language.' },

  { categoryId: 'registration', text: 'How many registrations were completed today?' },
  { categoryId: 'registration', text: 'How many registrations were completed this week?' },
  { categoryId: 'registration', text: 'Compare registrations this week vs last week.' },
  {
    categoryId: 'registration',
    text: 'Which patients completed registration but are missing insurance?',
  },
  { categoryId: 'registration', text: 'Show registrations by state.' },
  { categoryId: 'registration', text: 'Show registration completion trend by week.' },

  { categoryId: 'appointments', text: "Show me today's appointments." },
  { categoryId: 'appointments', text: "Show me this week's appointments." },
  { categoryId: 'appointments', text: 'Which appointments are pending confirmation?' },
  { categoryId: 'appointments', text: 'Which appointments are no-show?' },
  { categoryId: 'appointments', text: 'Which appointments have no consent signed?' },
  { categoryId: 'appointments', text: 'Which appointments have eligibility not verified?' },
  { categoryId: 'appointments', text: 'Which appointments have SOAP note not submitted?' },
  { categoryId: 'appointments', text: 'Show appointment status breakdown for this month.' },
  { categoryId: 'appointments', text: 'Show appointments by provider this week.' },
  { categoryId: 'appointments', text: 'Which patients have more than one appointment scheduled?' },

  { categoryId: 'billing', text: 'Show billing encounters missing progress notes.' },
  { categoryId: 'billing', text: 'Which encounters are ready for billing?' },
  { categoryId: 'billing', text: 'Which billing records have eligibility failed or disapproved?' },
  { categoryId: 'billing', text: 'Show billing status breakdown this month.' },
  { categoryId: 'billing', text: 'Which providers have the most unsubmitted progress notes?' },
  { categoryId: 'billing', text: 'Show confirmed visits where progress note is not submitted.' },
  {
    categoryId: 'billing',
    text: 'Show patients with verified eligibility but missing progress note.',
  },
  { categoryId: 'billing', text: 'Show encounters where insurance is missing or listed as N/A.' },
  { categoryId: 'billing', text: 'Show billing records by provider.' },
  { categoryId: 'billing', text: 'What is blocking billing this week?' },

  { categoryId: 'careCoord', text: 'Show patients needing follow-up tracking.' },
  { categoryId: 'careCoord', text: 'Which patients have follow-up tracking started?' },
  { categoryId: 'careCoord', text: 'Show follow-up status breakdown.' },
  { categoryId: 'careCoord', text: 'Which patients need labs follow-up?' },
  { categoryId: 'careCoord', text: 'Which patients need scheduling follow-up?' },
  { categoryId: 'careCoord', text: 'Which follow-up tasks are overdue?' },
  { categoryId: 'careCoord', text: 'Which patients need staff action today?' },
  { categoryId: 'careCoord', text: 'Which patients are blocked from moving forward?' },
  { categoryId: 'careCoord', text: 'What are the top care coordination issues this week?' },
  { categoryId: 'careCoord', text: 'Show care coordination workload by provider.' },

  { categoryId: 'fax', text: 'Show latest incoming faxes.' },
  { categoryId: 'fax', text: 'Which faxes are not assigned to a patient?' },
  { categoryId: 'fax', text: 'Which faxes are missing referring provider?' },
  { categoryId: 'fax', text: 'Which lab results came by fax this week?' },
  { categoryId: 'fax', text: 'Show faxes received today.' },
  { categoryId: 'fax', text: 'Which faxes still need staff review?' },
  { categoryId: 'fax', text: 'Show documents added for a patient this week.' },
  { categoryId: 'fax', text: 'Which documents are progress notes?' },
  { categoryId: 'fax', text: 'Which documents are missing titles?' },
  { categoryId: 'fax', text: 'Show document volume by type.' },

  { categoryId: 'messages', text: 'Show active message threads.' },
  { categoryId: 'messages', text: 'Which message threads are marked urgent?' },
  { categoryId: 'messages', text: 'Which message threads need follow-up?' },
  { categoryId: 'messages', text: 'Which patients have active messages?' },
  { categoryId: 'messages', text: 'Which messages were received today?' },
  { categoryId: 'messages', text: 'Which messages were received this week?' },
  { categoryId: 'messages', text: 'Show unread or unresolved message threads.' },
  { categoryId: 'messages', text: 'Show message volume by week.' },

  { categoryId: 'patientSummary', text: 'Give me clinical summary for a patient.' },
  { categoryId: 'patientSummary', text: 'What is missing before billing?' },
  { categoryId: 'patientSummary', text: 'Is this patient ready for lab follow-up?' },
  { categoryId: 'patientSummary', text: 'Is this patient ready for billing?' },
  { categoryId: 'patientSummary', text: 'Show all open issues for this patient.' },
  { categoryId: 'patientSummary', text: 'Does this patient need care coordination follow-up?' },
  { categoryId: 'patientSummary', text: 'Has status tracking been started for this patient?' },
  { categoryId: 'patientSummary', text: 'Does this patient have consent signed?' },

  { categoryId: 'clinical', text: 'Does the patient have diabetes?' },
  { categoryId: 'clinical', text: 'Does the patient have hypertension?' },
  { categoryId: 'clinical', text: 'What medications are active?' },
  { categoryId: 'clinical', text: 'Were PSA labs ordered?' },
  { categoryId: 'clinical', text: 'Show abnormal labs over time.' },
  { categoryId: 'clinical', text: 'What ICD-10 codes are listed for this patient?' },
  { categoryId: 'clinical', text: "Show this patient's active lab orders." },
  { categoryId: 'clinical', text: 'Are there duplicate lab orders for this patient?' },
  { categoryId: 'clinical', text: 'Compare latest lab results to previous lab results.' },
  { categoryId: 'clinical', text: 'Show all imaging results for this patient.' },

  { categoryId: 'graphs', text: 'Graph registrations by day for the last 30 days.' },
  { categoryId: 'graphs', text: 'Graph patients by state.' },
  { categoryId: 'graphs', text: 'Graph appointments by status this month.' },
  { categoryId: 'graphs', text: 'Graph completed vs no-show appointments.' },
  { categoryId: 'graphs', text: 'Graph billing-ready encounters by week.' },
  { categoryId: 'graphs', text: 'Graph new patients by week.' },
  { categoryId: 'graphs', text: "Show KPI cards for today's operations." },
  { categoryId: 'graphs', text: "Show KPI cards for this week's appointments." },
  { categoryId: 'graphs', text: 'Show KPI cards for billing readiness.' },
  { categoryId: 'graphs', text: 'Show a dashboard summary for leadership.' },
]

export const WELCOME_CHIPS: string[] = [
  "Give me today's executive summary.",
  'What needs attention today?',
  'What is blocking patients from moving forward?',
  'Which patients are not billing-ready and why?',
  'Show me all patients missing insurance information.',
  'Show me all appointments that need follow-up tracking started.',
]
